import {set} from 'lodash';
import React, {useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';
import {getChampionshipCalendarByChampionshipId} from '../../../../pages/Tools/Tds/Widgets/TdsList';
import ChallengeCommonSection from './components/ChallengeCommonSection';
import ChallengeInternationalSection from './components/ChallengeInternationalSection';
import {ICreateChallengeData} from './types';
import {getMPPChallenge, postImageUpload, updateMPPChallenge} from './api';
import {createMPPChallenge} from './api';
import {CreateChallengeStyles} from './styles';

export type MPPGameWeek = {
  gameWeekNumber: string;
  roundType?: string;
};

const MPPCreateChallengeEditor = () => {
  const dispatch = useDispatch();
  const [data, setData] = useState<ICreateChallengeData>({});
  const [championshipId, setChampionshipId] = useState(1);
  const [championshipCalendar, setChampionshipCalendar] = useState<MPPGameWeek[]>([]);
  const [selectedLanguage, setSelectedLanguage] = useState('fr-FR');
  const [selectedTab, setSelectedTab] = React.useState<'write' | 'preview'>(
    'write',
  );
  const [editedChallengeId, setEditedChallengeId] = useState(undefined);
  const [challengeIdInput, setChallengeIdInput] = useState('');

  const [mode, setMode] = useState('creation');

  const fetchExistingChallengeData = () => {
    dispatch(
      getMPPChallenge(
        selectedLanguage,
        {
          contestId: challengeIdInput,
        },
        data => {
          if (data) {
            setData(data);
            setEditedChallengeId(data.customShortChallengeId);
          }
        },
        () => {
          setEditedChallengeId(undefined);
          setData({});
          alert('Aucun challenge trouvé');
        },
      ),
    );
  };

  const dataByLanguage = data?.localization?.[selectedLanguage];

  const onChange = (
    key: string,
    value:
      | string
      | number
      | boolean
      | {
          doublePoints: number;
        }
      | undefined,
  ) => {
    setData({
      ...set(data, key, value !== '' ? value : undefined),
    });
  };

  const fetchAllData = () => {
    dispatch(
      getChampionshipCalendarByChampionshipId({
        championshipId,
        onSuccess: (championshipCalendar) => {
          const gameweeks: MPPGameWeek[] = []
          Object.keys(championshipCalendar.gameWeeks).forEach((key) => {
            const gameweek = championshipCalendar.gameWeeks[key];
            gameweeks.push(gameweek);
          });

          setChampionshipCalendar(gameweeks);
        }
      }, true),
    );
  };

  useEffect(() => {
    fetchAllData();
  }, [championshipId]);

  // Sets lastGameWeekNumber value >= firstGameWeekNumber.
  useEffect(() => {
    onChange('lastGameWeekNumber', Number(data?.firstGameWeekNumber));
  }, [data?.firstGameWeekNumber]);

  const onPostUpload = (acceptedFile, cb) =>
    dispatch(postImageUpload(acceptedFile, cb));

  const payload = {
    championshipId: championshipId,
    firstGameWeekNumber: data?.firstGameWeekNumber,
    lastGameWeekNumber: data?.lastGameWeekNumber,
    bonuses: {
      doublePoints: data.bonuses || 1,
    },
    isSupervised: true,
    name: dataByLanguage?.name,
    imageUrl: dataByLanguage?.imageUrl,
    isPinned: data?.isPinned || false,
    isSuperPinned: true,
    description: dataByLanguage?.description,
    supervisedDescription: dataByLanguage?.supervisedDescription,
    backgroundImageUrl: dataByLanguage?.backgroundImageUrl,
    presentationImageUrl: dataByLanguage?.presentationImageUrl,
    customShortChallengeId: data?.customShortChallengeId,
  };

  const handleNewChallengeCreation = () => {
    dispatch(
      createMPPChallenge(
        payload,
        () => alert('Challenge crée!'),
        () => alert('error'),
      ),
    );
  };

  const handleChallengeEdition = () => {
    dispatch(
      updateMPPChallenge(
        payload,
        editedChallengeId,
        () => alert('Challenge modifié !'),
        () => alert('error'),
      ),
    );
  };

  return (
    <>
      <h3 className="box-title">
        Add/Edit Challenge{' '}
        <small
          style={{
            marginLeft: 2,
          }}>
          MPP
        </small>
      </h3>
      <div className="box">
        <div
          style={{
            flexDirection: 'row',
            width: '100%',
            marginTop: 8,
            justifyContent: 'center',
            marginBottom: 8,
            display: 'flex',
          }}>
          <span
            onClick={() => {
              setMode('creation');
              setChallengeIdInput('');
              setEditedChallengeId(undefined);
              setData({});
            }}
            style={{
              ...CreateChallengeStyles.tab,
              ...(mode === 'creation' && CreateChallengeStyles.selectedTab),
            }}>
            Création
          </span>
          <span
            onClick={() => setMode('edition')}
            style={{
              ...CreateChallengeStyles.tab,
              ...(mode === 'edition' && CreateChallengeStyles.selectedTab),
            }}>
            Edition
          </span>
        </div>
        {mode === 'edition' ? (
          <div
            style={{
              display: 'flex',
              width: '100%',
              justifyContent: 'center',
              paddingBottom: 16,
            }}>
            <input
              value={challengeIdInput}
              onChange={event => setChallengeIdInput(event.target.value)}
            />
            <button
              style={{marginLeft: 8}}
              className="btn btn-warning"
              onClick={fetchExistingChallengeData}>
              Rechercher
            </button>
          </div>
        ) : null}
        {(mode === 'edition' && !!editedChallengeId) || mode === 'creation' ? (
          <div className="row">
            <div className="col-md-6">
              <ChallengeInternationalSection
                selectedLanguage={selectedLanguage}
                dataByLanguage={dataByLanguage}
                onChange={onChange}
                editedChallengeId={editedChallengeId}
                selectedTab={selectedTab}
                setSelectedTab={setSelectedTab}
                onPostUpload={onPostUpload}
              />
            </div>
            <div className="col-md-6">
              <ChallengeCommonSection
                setChampionshipId={setChampionshipId}
                championshipId={championshipId}
                gameWeeks={championshipCalendar}
                onChange={onChange}
                editedChallengeId={editedChallengeId}
                firstGameWeekNumber={data?.firstGameWeekNumber}
                lastGameWeekNumber={data?.lastGameWeekNumber}
                isPinned={data?.isPinned}
                bonuses={data?.bonuses}
                customShortChallengeId={data?.customShortChallengeId}
                isSuperPinned={data?.isSuperPinned}
              />
              <div
                style={{
                  marginTop: 16,
                }}>
                <button
                  className="btn btn-success m-3"
                  onClick={
                    mode === 'edition'
                      ? handleChallengeEdition
                      : handleNewChallengeCreation
                  }>
                  Enregister
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
};

export default MPPCreateChallengeEditor;



// WEBPACK FOOTER //
// ./src/pages/Other/MPP/MPPCreateChallengeEditor/index.tsx